import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Trash2, ExternalLink, MapPin, Mountain, Factory, Thermometer, BookOpen, Wrench, Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import SortHeader, { useSort, sortArr } from '../components/SortHeader';

const CATEGORIES = {
  cadastre: { label: 'Cadastre', icon: MapPin, color: 'border-blue-300 bg-blue-50 text-[#002FA7]' },
  topo: { label: 'Plan topo', icon: Mountain, color: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
  icpe: { label: 'ICPE', icon: Factory, color: 'border-rose-300 bg-rose-50 text-rose-800' },
  thermique: { label: 'Thermique', icon: Thermometer, color: 'border-orange-300 bg-orange-50 text-orange-800' },
  reglementation: { label: 'Réglementation', icon: BookOpen, color: 'border-indigo-300 bg-indigo-50 text-indigo-800' },
  outil: { label: 'Outil', icon: Wrench, color: 'border-violet-300 bg-violet-50 text-violet-800' },
  autre: { label: 'Autre', icon: Bookmark, color: 'border-zinc-300 bg-zinc-50 text-zinc-700' },
};

export default function LiensUtiles() {
  const { user } = useAuth();
  const [liens, setLiens] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ titre: '', url: '', categorie: 'autre', description: '' });
  const [sort, toggleSort] = useSort('ajoute_le', 'desc');

  const load = async () => {
    const { data } = await api.get('/liens-utiles');
    setLiens(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return liens
      .filter(l => filterCat === 'all' || l.categorie === filterCat)
      .filter(l => !s || l.titre.toLowerCase().includes(s) || (l.description || '').toLowerCase().includes(s) || l.url.toLowerCase().includes(s));
  }, [liens, search, filterCat]);

  const sorted = useMemo(() => sortArr(filtered, sort, (l, k) => l[k]), [filtered, sort]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titre || !form.url) { toast.error('Titre et URL requis'); return; }
    let url = form.url.trim();
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
    try {
      await api.post('/liens-utiles', { ...form, url });
      toast.success('Lien ajouté');
      setOpen(false);
      setForm({ titre: '', url: '', categorie: 'autre', description: '' });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce lien ?')) return;
    try {
      await api.delete(`/liens-utiles/${id}`);
      toast.success('Supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // Compteurs par catégorie pour les filtres
  const counts = useMemo(() => {
    const c = { all: liens.length };
    for (const k of Object.keys(CATEGORIES)) c[k] = liens.filter(l => l.categorie === k).length;
    return c;
  }, [liens]);

  return (
    <Layout
      title="Liens utiles"
      subtitle="Bonnes pratiques & ressources partagées par l'équipe"
      actions={
        <Button onClick={() => setOpen(true)} className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="add-lien">
          <Plus size={14} className="mr-1" /> Ajouter un lien
        </Button>
      }
    >
      {/* Filtres catégories — chips */}
      <div className="bg-white border border-zinc-200 p-3 mb-4 flex flex-wrap gap-2" data-testid="categories-filter">
        <button onClick={() => setFilterCat('all')}
          className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${filterCat === 'all' ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'}`}>
          Tous ({counts.all})
        </button>
        {Object.entries(CATEGORIES).map(([k, c]) => {
          const Icon = c.icon;
          return (
            <button key={k} onClick={() => setFilterCat(k)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border inline-flex items-center gap-1.5 ${filterCat === k ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'}`}
              data-testid={`cat-filter-${k}`}>
              <Icon size={12} /> {c.label} ({counts[k] || 0})
            </button>
          );
        })}
      </div>

      {/* Recherche */}
      <div className="bg-white border border-zinc-200 p-4 mb-4">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-[11px] text-zinc-400" />
          <Input className="rounded-none pl-9" placeholder="Rechercher titre, description, URL…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="liens-search" />
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="liens-table">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-900">
            <tr className="text-left">
              <SortHeader sort={sort} onToggle={toggleSort} k="titre" label="Titre" />
              <SortHeader sort={sort} onToggle={toggleSort} k="categorie" label="Catégorie" />
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Description</th>
              <SortHeader sort={sort} onToggle={toggleSort} k="ajoute_par_nom" label="Ajouté par" />
              <SortHeader sort={sort} onToggle={toggleSort} k="ajoute_le" label="Le" />
              <th className="w-[80px]"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((l) => {
              const cat = CATEGORIES[l.categorie] || CATEGORIES.autre;
              const Icon = cat.icon;
              const canDelete = user?.role === 'admin' || l.ajoute_par_id === user?.id;
              return (
                <tr key={l.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`lien-${l.id}`}>
                  <td className="px-3 py-2">
                    <a href={l.url} target="_blank" rel="noopener noreferrer"
                      className="font-medium text-[#002FA7] hover:underline inline-flex items-center gap-1">
                      {l.titre} <ExternalLink size={11} />
                    </a>
                    <div className="font-mono text-[10px] text-zinc-400 truncate max-w-[280px]">{l.url}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${cat.color}`}>
                      <Icon size={11} /> {cat.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-700 max-w-[300px]">{l.description || <span className="text-zinc-400 italic">—</span>}</td>
                  <td className="px-3 py-2 text-xs text-zinc-600">{l.ajoute_par_nom || '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-500">{(l.ajoute_le || '').slice(0, 10)}</td>
                  <td className="px-3 py-2 text-right">
                    {canDelete && (
                      <button onClick={() => remove(l.id)} className="p-1 hover:bg-rose-100 text-rose-600" title="Supprimer">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500 text-sm">
                Aucun lien {search || filterCat !== 'all' ? 'ne correspond aux filtres.' : 'pour l’instant. Ajoutez-en un avec le bouton « + Ajouter un lien » en haut à droite.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-xl">Nouveau lien utile</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input className="rounded-none" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Cadastre.gouv.fr" data-testid="lien-titre" />
            </div>
            <div>
              <Label>URL *</Label>
              <Input className="rounded-none" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://www.cadastre.gouv.fr" data-testid="lien-url" />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                <SelectTrigger className="rounded-none" data-testid="lien-categorie"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {Object.entries(CATEGORIES).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optionnel)</Label>
              <Textarea className="rounded-none" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="À quoi sert ce lien, quand l’utiliser…" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="lien-submit">Ajouter</Button>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-zinc-900">Annuler</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
